import { apiSlice, postImage, useImagesQuery } from '../../api/api'
import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { FileUploader } from 'react-drag-drop-files'
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg'
import './media-library.scss'
import { useDispatch } from 'react-redux'
import Modal from 'react-modal'
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner'
import { ReactComponent as FolderIcon } from 'assets/icons/folder.svg'
import { ReactComponent as FolderOpenIcon } from 'assets/icons/folder-open.svg'

const folders = [
  {
    id: 'general',
    name: 'General',
  },
  {
    id: 'albumArt',
    name: 'Album Art',
  },
  {
    id: 'backgrounds',
    name: 'Backgrounds',
  },
]
const MediaLibrary = ({
  isOpen,
  onCancel,
  onSubmit,
  type,
  showRemoveImage,
}) => {
  const uploadRef = useRef({})
  const dispatch = useDispatch()
  const [uploadState, setUploadState] = useState({})
  const [selectedImage, setSelectedImage] = useState(null)
  const [sortOrder, setSortOrder] = useState('uploaded')
  const [error, setError] = useState(null)
  const [folder, setFolder] = useState(type)
  const { data: imagesData, isFetching: imagesIsFetching } =
    useImagesQuery(folder)
  const noImage = {
    url: '',
    name: 'No Image',
  }
  const uploadingImages = Object.values(uploadState).filter(
    (upload) => upload.status === 'uploading',
  )

  const images =
    imagesData?.length > 0
      ? [...imagesData].sort((a, b) => {
          if (sortOrder === 'uploaded') {
            return (
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            )
          }
          return b.name - a.name
        })
      : []

  useEffect(() => {
    const interval = setInterval(() => {
      setUploadState(uploadRef.current)
    }, 100)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    setSelectedImage(null)
  }, [isOpen])

  function handleUpload(file) {
    const now = Date.now()
    const formData = new FormData()
    setError(null)
    formData.append('file', file)

    postImage(type, formData, (progressEvent) => {
      const progress = Math.round(
        (100 * progressEvent.loaded) / progressEvent.total,
      )
      uploadRef.current = {
        ...uploadRef.current,
        [now]: {
          name: file?.name,
          status: 'uploading',
          progress,
        },
      }
    })
      .then((res) => {
        uploadRef.current = {
          ...uploadRef.current,
          [now]: {
            name: file?.name,
            progress: 100,
            id: res?.data?.id,
            status: 'complete',
          },
        }
        dispatch(apiSlice.util.invalidateTags([{ type: 'Images', id: 'LIST' }]))
      })
      .catch((err) => {
        uploadRef.current = {
          ...uploadRef.current,
          [now]: {
            name: file?.name,
            progress: 100,
            status: 'error',
          },
        }
        dispatch(apiSlice.util.invalidateTags([{ type: 'Images', id: 'LIST' }]))
        if (err?.response?.data?.error === 'spaceExceeded') {
          setError({
            title: 'Media Storage Full',
            description:
              "You've run out of space, delete some images in order to upload more.",
          })
        }
      })
  }

  function handleSortChange(evt) {
    setSortOrder(evt.target.value)
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onCancel}
      className="modal p-0"
      overlayClassName="modal-overlay"
    >
      <div className="media-library flex flex-col h-[660px] w-[1060px]">
        <div className="bg-gray-dark px-6 pt-6 pb-4 flex justify-center">
          <h2 className="flex-fill">Media Library</h2>
          <div className="relative">
            <button
              className="p-4 absolute top-[-25px] right-[-20px]"
              onClick={onCancel}
            >
              <CloseIcon fill="#CCC" />
            </button>
          </div>
        </div>
        {!!error && (
          <div className="px-6 bg-gray-dark mt-2">
            <div className="p-4 bg-pink-dark rounded border border-pink-error mb-4">
              <h4>{error.title}</h4>
              <p>{error.description}</p>
            </div>
          </div>
        )}
        <div className="flex flex-fill bg-gray-dark min-h-0">
          <div className="flex-fill p-0 flex flex-col">
            <div className="flex p-2">
              <div className="media-library-uploader flex-fill">
                <FileUploader
                  name="file-library-upload"
                  handleChange={handleUpload}
                  types={['JPG', 'PNG', 'GIF']}
                ></FileUploader>
              </div>
              <div className="sort-by">
                <label>Sort By</label>
                <select onChange={handleSortChange}>
                  <option value="uploaded">Uploaded</option>
                  <option value="alpha">Alphabetical</option>
                </select>
              </div>
            </div>
            <div className="flex flex-fill">
              <div className="w-[170px] flex flex-col pt-4 pl-6 mt-2">
                {folders.map((option) => (
                  <div className="mb-4" key={option.id}>
                    <button
                      className="flex items-center"
                      onClick={() => setFolder(option.id)}
                    >
                      {option.id === folder ? (
                        <FolderOpenIcon fill="#ccc" height="24" />
                      ) : (
                        <FolderIcon fill="#ccc" height="24" />
                      )}

                      <span
                        className={clsx('inline-block ml-2 font-bold text-sm', {
                          'text-pink': option.id === folder,
                        })}
                      >
                        {option.name}
                      </span>
                    </button>
                  </div>
                ))}
              </div>
              <div className=" p-2 flex flex-fill flex-wrap overflow-y-scroll overflow-x-hidden content-start">
                {showRemoveImage && (
                  <div className="p-2">
                    <button
                      onClick={() => setSelectedImage(noImage)}
                      onDoubleClick={() => onSubmit(noImage)}
                      className={clsx('p-2', {
                        'rounded bg-grey outline-pink outline':
                          selectedImage?.url !== undefined &&
                          selectedImage.url === noImage.url,
                      })}
                    >
                      <CloseIcon width="100" height="100" fill="#aaa" />

                      <div className="mt-2 truncate w-[100px] text-sm">
                        {noImage.name}
                      </div>
                    </button>
                  </div>
                )}
                {uploadingImages?.map((image) => (
                  <div className="p-4" key={image.name}>
                    <div className="w-[100px] h-[100px] flex items-center justify-center">
                      <div className="">uploading</div>
                    </div>
                    <div className="mt-2 truncate w-[100px] text-sm">
                      {image.name}
                    </div>
                  </div>
                ))}
                {imagesIsFetching ? (
                  <div className="flex justify-center items-center h-full w-full flex-fill">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <>
                    {images?.map((image) => (
                      <div className="p-2 h-[155px] w-[135px]" key={image.url}>
                        <button
                          onClick={() => setSelectedImage(image)}
                          onDoubleClick={() => onSubmit(image)}
                          className={clsx('p-2', {
                            'rounded bg-grey outline-pink outline':
                              selectedImage?.url === image.url,
                          })}
                        >
                          <img
                            src={image.url}
                            className="w-[100px] h-[100px] object-cover rounded"
                          />
                          <div className="mt-2 truncate w-[100px] text-sm">
                            {image.name}
                          </div>
                        </button>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="w-[300px] bg-deep flex flex-col p-6">
            <div className="flex-fill">
              {selectedImage && (
                <>
                  {!!selectedImage.url ? (
                    <img
                      src={selectedImage.url}
                      className="w-[250px] h-[250px] object-cover rounded"
                    />
                  ) : (
                    <CloseIcon width="250" height="250" fill="#aaa" />
                  )}

                  <div className="mt-4 font-bold">{selectedImage.name}</div>
                </>
              )}
            </div>
            {selectedImage && (
              <div className="flex justify-end">
                <button className="button" onClick={onCancel}>
                  Cancel
                </button>
                <button
                  className="button primary"
                  onClick={() => onSubmit(selectedImage)}
                >
                  Select
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default MediaLibrary
